



















































































































































import { UserCapabilityOption } from "@/data/models/UserCapabilityOption";
import { getRoleLabel, getRoleActive, createDefaultRoleSettings, getCapabilitiesThatRequireBasicLicence } from "@/data/models/RoleSettings";
import { ADMINISTRATION_USER_CAPABILITIES, ADMIN_PORTAL_USER_CAPABILITIES, UserCapability } from "@/data/models/UserCapability";
import { localiceCapability } from "@/utils/LocalisationHelper";
import router from "@/router/router";
import { Role } from "@/data/models/User";
import { RoleSettingsModule } from "@/store/modules/RoleSettingsModule";
import { Vue, Component, Ref } from "vue-property-decorator";
import { saveRoleSettings } from "@/data/repository/RoleSettingsRepository";
import LicenceUsageDetailsComponent from "../licences/LicenceUsageDetails.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { getProjectManagementType, isMemberClient } from "@/data/repository/ClientRepository";
import { ProjectManagement } from "@/data/models/Client";
import { defineOwnRoleSettingsClientAccess } from "@/permissions/ClientPermissions"
import { configureAdministrationCapabilitiesAccess, configureAdminPortalCapabilitiesAccess } from "@/permissions/UserPermissions";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK, COLOR_PRIMARY } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    "licence-usage-details": LicenceUsageDetailsComponent,
  },
})
export default class PermissionTableComponent extends Vue {
  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Ref("licenceUsageDetails")
  licenceUsageDetails!: LicenceUsageDetailsComponent;

  roles = Object.values(Role);

  capabilityOptions = [
    {
      value: UserCapabilityOption.YES,
      label: this.$t("settings.roleManagement.capabilityOption.yes").toString(),
    },
    {
      value: UserCapabilityOption.NO,
      label: this.$t("settings.roleManagement.capabilityOption.no").toString(),
    },
  ];

  extendedCapabilityOptions = [
    {
      value: UserCapabilityOption.YES,
      label: this.$t("settings.roleManagement.capabilityOption.yes").toString(),
    },
    {
      value: UserCapabilityOption.NO,
      label: this.$t("settings.roleManagement.capabilityOption.no").toString(),
    },
    {
      value: UserCapabilityOption.CONDITIONAL,
      label: this.$t(
        "settings.roleManagement.capabilityOption.conditional"
      ).toString(),
    },
  ];
  
  get defaultConfiguration() {
    return createDefaultRoleSettings();
  }

  get capabilitiesThatRequireBasicLicence() {
    return getCapabilitiesThatRequireBasicLicence();
  }

  get currentConfiguration() {
    return RoleSettingsModule.roleSettings; 
  }

  get projectManagementType() {
    return getProjectManagementType();
  }

  isLoading = false;
  hasChanged = false;
  shouldReloadClientPanels = false;

  showingSuccessSnackbar = false;
  successSnackbarText = "";

  cancelChanges = false;

  editDevicesCapability = UserCapability.EDIT_DEVICES;

  /** Table header background: light grey in light mode, dark elevated surface in dark mode. */
  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isSelectionDisabled() {
    return !defineOwnRoleSettingsClientAccess(true);
  }

  get isMemberClient() {
    return isMemberClient();
  }

  get memberClientHintColor() {
    return COLOR_PRIMARY;
  }

  doesCapabilityDependOnBasicLicence(capability: UserCapability) {
    return this.capabilitiesThatRequireBasicLicence.includes(capability);
  }

  disableCapabilityIfNoBasicLicence(capability: UserCapability, role: Role) {
    return ((
      this.doesCapabilityDependOnBasicLicence(capability)
      && this.currentConfiguration.roleToConfiguration[role].capabilityMap[UserCapability.BASIC_LICENCE] === UserCapabilityOption.NO
    ) ||
    (
      this.doesCapabilityDependOnBasicLicence(capability)
      && !this.currentConfiguration.roleToConfiguration[role].capabilityMap[UserCapability.BASIC_LICENCE]
      && this.defaultConfiguration.roleToConfiguration[role].capabilityMap[UserCapability.BASIC_LICENCE] === UserCapabilityOption.NO
    ));
  }

  getCapabilities() {
    return UserCapability;
  }

  getCapabilityOptions() {
    return UserCapabilityOption;
  }

  getRoleLabel(role: Role) {
    return getRoleLabel(role, this);
  }

  getRoleActive(role: Role) {
    return getRoleActive(role, this);
  }

  getRoleCapability(role: Role) {
    const capabilityMap =
      this.currentConfiguration.roleToConfiguration[role].capabilityMap;

    const defaultCapabilityMap = 
      this.defaultConfiguration.roleToConfiguration[role].capabilityMap;

    return this.allCapabilities.map((capability) => {
      return {
        capability,
        capabilityOption: capabilityMap[capability] ? capabilityMap[capability] : defaultCapabilityMap[capability],
      };
    })
    .filter((it) => {
      return !this.hideOtherLicences(it.capability) && this.isAllowedToConfigureRestrictedUserCapabilities(it.capability);
    });
  }

  /**
   * Whether the current user may configure this capability at all.
   *
   * Two capability groups are restricted rather than universally editable: the administration ones
   * (multi-tenant management) and the admin-portal ones (roles inside the IRES admin portal, e.g.
   * Drying Expert). Everything else is configurable by anyone who can reach the role editor.
   */
  isAllowedToConfigureRestrictedUserCapabilities(capability: UserCapability) {
    const activeUser = this.activeUser;

    return (
      (!ADMINISTRATION_USER_CAPABILITIES.includes(capability) || configureAdministrationCapabilitiesAccess(activeUser)) &&
      (!ADMIN_PORTAL_USER_CAPABILITIES.includes(capability) || configureAdminPortalCapabilitiesAccess(activeUser))
    );
  }

  hideOtherLicences(capability: UserCapability) {
    if (
      (
        capability === UserCapability.DRYING_AND_REPAIR ||
        capability === UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION
      ) &&
      (
        this.projectManagementType === ProjectManagement.DEVICES_ONLY_WEBSITE ||
        this.projectManagementType === ProjectManagement.WEBSITE
      )
    ) {
      return true;
    }

    return false;
  }

  changeValue(
    selectedCapabilityOption: UserCapabilityOption,
    role: Role,
    capability: UserCapability
  ) {
    const currentRoleSettings = this.currentConfiguration.roleToConfiguration[role];
    const currentCapabilityMap = currentRoleSettings.capabilityMap;

    currentRoleSettings.capabilityMap = {
      ...currentCapabilityMap,
      [capability]: selectedCapabilityOption,
    };

    this.hasChanged = true;
    this.shouldReloadClientPanels = capability === UserCapability.DEVICE_ADMINISTRATION;

    // To make sure if previously this role was set to inactive, it gets the active value true
    if (!this.currentConfiguration.roleToConfiguration[Role.CUSTOM_ROLE_5].roleActive) {
      (this.currentConfiguration.roleToConfiguration[Role.CUSTOM_ROLE_5].roleActive as boolean) = true;
    }

    if (capability === UserCapability.BASIC_LICENCE && selectedCapabilityOption === UserCapabilityOption.NO) {
      this.setCapabilitiesThatDependOnBasicLicenceToNo(role);
    }
  }

  setCapabilitiesThatDependOnBasicLicenceToNo(role: Role) {
    const currentRoleSettings = this.currentConfiguration.roleToConfiguration[role];
    const currentCapabilityMap = currentRoleSettings.capabilityMap;

    this.capabilitiesThatRequireBasicLicence.forEach(capability => {
      (currentCapabilityMap[capability] as UserCapabilityOption) = UserCapabilityOption.NO;
    });
  }

  async onSave() {
    this.isLoading = true;
    const roleSettingsUpdate = this.currentConfiguration;

    const result = await saveRoleSettings(
      roleSettingsUpdate,
      true
    );

    if (result) {
      this.isLoading = false;
    }

    if (result.wasSuccessful) {
      if (this.shouldReloadClientPanels) {
        localStorage.setItem("shouldReloadClientPanels", "true");
        localStorage.setItem("shouldReloadClientPanelsForCockpit", "true");

        localStorage.removeItem("branchFilter");
        localStorage.removeItem("clientFilter");
      }

      this.showingSuccessSnackbar = true;
      this.successSnackbarText = this.$t("common.changesSaved").toString();
      this.hasChanged = false;
    }

    if (result.licenceInfo != null) {
      await this.licenceUsageDetails.show(
        result.licenceInfo
      );
    }
  }

  onReturn() {
    router.back();
  }

  async onCancel() {
    const result = await this.confirmationDialogComponent.show({
      title: this.$t("common.confirmationDialog.cancelTitle").toString(),
      message: this.$t("common.confirmationDialog.cancelMessage").toString(),
    });

    if (result) {
      this.cancelChanges = true;
      router.replace({ path: `/main/settings/roleManagement` });
    }
  }

  isRoleDeviceManager(role: Role) {
    if (role == Role.CUSTOM_ROLE_5) {
      return true;
    }
    return false;
  }

  isBasicLicence(role: Role, capability: UserCapability) {
    if (role == Role.CUSTOM_ROLE_5 && capability == UserCapability.BASIC_LICENCE) {
      return true;
    }
    return false;
  }

  hideAdditionalSelectionOptions(capability: UserCapability) {
    return !(
      capability === UserCapability.LEAK_DETECTION ||
      capability === UserCapability.DRYING ||
      capability === UserCapability.DAMAGE_ASSESSMENT_LARGE ||
      capability === UserCapability.REPAIR ||
      capability === UserCapability.HAS_VEHICLE
    );
  }

  get allCapabilities() {
    return Object.values(UserCapability);
  } 

  get filteredCapabilities() {
    return this.allCapabilities.filter((capability) => {
      if (
        (
          capability === UserCapability.DRYING_AND_REPAIR ||
          capability === UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION
        ) &&
        (
          this.projectManagementType === ProjectManagement.DEVICES_ONLY_WEBSITE ||
          this.projectManagementType === ProjectManagement.WEBSITE
        )
      ) {
        return false;
      }

      return this.isAllowedToConfigureRestrictedUserCapabilities(capability);
    });
  }

  get permissionNames() {
    return [
      ...this.filteredCapabilities.map((capability) =>
        localiceCapability(capability, this)
      ),
    ];
  } 

  mounted() {
    this.setToolbarTitle();
  }

  setToolbarTitle() {
    const settingsItemTitle = this.isSelectionDisabled
      ? this.$t("settings.roleManagement.titleAlt").toString()
      : this.$t("settings.roleManagement.title").toString();

    const baseTitle = this.$t("pages.settings").toString() + " > " + settingsItemTitle;
    const currentPageTitle = this.isSelectionDisabled
      ? this.$t("settings.roleManagement.viewRolesConfiguration").toString()
      : this.$t("settings.roleManagement.edit").toString();

    const toolbarTitle = baseTitle + " > " + currentPageTitle;

    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }
}
